import { Controller } from "@hotwired/stimulus"
import { SELECTION_EVENT } from "../selection/selection_events"

/**
 * Single-slot picker: listens for item:selected, stores the id in a hidden
 * field, and can open a side-panel picker scoped to this slot.
 *
 * Values:
 *   name         – receiver name, used for target-based event filtering
 *   pickerUrl    – base URL for the turbo-frame picker (openPicker appends ?slot=name)
 *   selectedId   – currently selected id (synced to hidden field)
 *   dismissEvent – optional window CustomEvent to fire after selection
 *
 * Dispatches:
 *   "changed" – after storing the id, with { type, id } in detail.
 *               Parent controllers can listen via
 *               data-action="selection-receiver:changed->my-ctrl#onChanged"
 */
export default class extends Controller {
  static values = {
    name: String,
    pickerUrl: String,
    selectedId: { type: Number, default: 0 },
    dismissEvent: String,
  }

  static targets = ["field"]

  connect() {
    this._handleSelected = this.handleSelected.bind(this);
    document.addEventListener(SELECTION_EVENT, this._handleSelected);
  }

  disconnect() {
    document.removeEventListener(SELECTION_EVENT, this._handleSelected);
  }

  openPicker() {
    const frame = document.querySelector("turbo-frame#selection_contents");
    if (frame) {
      const url = new URL(this.pickerUrlValue, window.location.origin);
      url.searchParams.set("slot", this.nameValue);
      frame.src = url.toString();
    }
    window.dispatchEvent(new CustomEvent("open-side-panel"));
  }

  handleSelected(event) {
    const { type, id, target, meta } = event.detail;

    // Ignore events not addressed to us
    if (target && this.hasNameValue && target !== this.nameValue) return;

    // Consume the event so duplicate receivers (same name, different DOM location) don't fire
    event.stopImmediatePropagation();

    this.selectedIdValue = id;

    if (this.hasFieldTarget) {
      this.fieldTarget.value = String(id);
    }

    if (this.hasDismissEventValue) {
      window.dispatchEvent(new CustomEvent(this.dismissEventValue));
    }

    this.dispatch("changed", {
      detail: { type, id, ...(meta ? { meta } : {}) },
    });
  }
}
